import React from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, BookOpen, HelpCircle, Image as ImgIcon, Play } from "lucide-react";
import { SafeHTML } from "@/components/site/RichTextEditor";
import ShareReactBar from "@/components/site/ShareReactBar";

const FORMAT_ICON = { article: BookOpen, faq: HelpCircle, infographie: ImgIcon, video: Play };
const FORMAT_COLOR = {
  article: "bg-cef-blue text-white",
  faq: "bg-cef-yellow",
  infographie: "bg-cef-pink text-white",
  video: "bg-cef-green",
};

export default function PedagoDetail() {
  const { id } = useParams();
  const [item, setItem] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    api.get(`/pedago/${id}`).then((r) => setItem(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <h1 className="font-display font-black text-3xl" data-testid="pedago-not-found">Ressource introuvable</h1>
        <Link to="/comprendre" className="neo-btn bg-cef-yellow mt-4 inline-flex">← Retour</Link>
      </div>
    );
  }
  if (!item) return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">Chargement…</div>;

  const Icon = FORMAT_ICON[item.format] || BookOpen;
  const isHTML = (item.content || "").trim().startsWith("<");

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="pedago-detail">
      <Link to="/comprendre" className="inline-flex items-center gap-2 text-sm font-bold hover:underline mb-6" data-testid="back-to-comprendre">
        <ArrowLeft className="w-4 h-4" /> Toutes les ressources
      </Link>

      <span className={`chip inline-flex items-center gap-1.5 ${FORMAT_COLOR[item.format] || "bg-white"}`} data-testid="pedago-format">
        <Icon className="w-3.5 h-3.5" /> {item.format}
      </span>

      <h1 className="mt-3 font-display font-black text-3xl sm:text-5xl tracking-tighter leading-tight" data-testid="pedago-title">{item.title}</h1>

      {item.summary && (
        <p className="mt-4 text-lg leading-relaxed text-neutral-700" data-testid="pedago-summary">{item.summary}</p>
      )}

      {item.media_url && item.format === "video" && (
        <div className="mt-6 aspect-video border-2 border-black rounded-md overflow-hidden" data-testid="pedago-video">
          <iframe src={item.media_url} className="w-full h-full" title={item.title} allowFullScreen />
        </div>
      )}

      {item.media_url && item.format === "infographie" && (
        <img src={item.media_url} alt={item.title} className="mt-6 w-full border-2 border-black rounded-md shadow-brutal" data-testid="pedago-infographic" />
      )}

      {item.content && (
        <div className="mt-6" data-testid="pedago-content">
          {isHTML
            ? <SafeHTML html={item.content} className="prose-lg" />
            : <div className="prose max-w-none text-base leading-relaxed whitespace-pre-wrap">{item.content}</div>}
        </div>
      )}

      {item.media_url && !["video", "infographie"].includes(item.format) && (
        <a href={item.media_url} target="_blank" rel="noreferrer" className="neo-btn bg-cef-yellow mt-8 inline-flex" data-testid="pedago-media-link">
          Ouvrir la ressource ↗
        </a>
      )}

      {item.tags?.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-2">
          {item.tags.map((t) => <span key={t} className="chip bg-white">#{t}</span>)}
        </div>
      )}

      <ShareReactBar title={item.title} kind="Ressource" />
    </article>
  );
}
